import React, { useEffect, useState, useContext } from 'react';
import {
    AppBar,
    Avatar,
    IconButton,
    Toolbar,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import Lock from '@mui/icons-material/Lock';
import Menu from '@mui/icons-material/Menu';
import { LicenseIcon } from '@brightlayer-ui/icons-mui';
import ExitToApp from '@mui/icons-material/ExitToApp';
import { Spacer, UserMenu } from '@brightlayer-ui/react-components';
import { LocalStorage } from '../store/local-storage';
import { useDrawer } from '../contexts/drawerContextProvider';
import { logoutUser } from '../actions/LogoutAction'
import DeleteForeverTwoToneIcon from '@mui/icons-material/DeleteForeverTwoTone';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CallIcon from '@mui/icons-material/Call';
import useClasses from '../hooks/useClassesHook';
import { EditProfile } from '../pages/UserAccount/EditProfile';
import DeleteProfileComponent from '../pages/UserAccount/DeleteProfileComponent';
import { useLocation } from 'react-router-dom';
import { KeyboardBackspace } from '@mui/icons-material';
import { StateContext } from '../contexts/StateContext';
import { useKeycloak } from '../contexts/KeycloakContext';
import { useNavigate } from 'react-router';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useTranslation } from 'react-i18next';
import { useOktaAuth } from '@okta/okta-react';
import { getAuthMode, AUTH_MODES } from '../utils/authMode';
import { NotificationBell } from '../components/NotificationBell/NotificationBell';
import { PortalClientStorage } from '../services/client-storage/storage';
import { isDevPortalEnvironment } from '../utils/deploymentEnvironment';

export const openEmail = () => {
    window.location.href = "mailto:eas-support@eaton.com"
}

export const callSupportContact = () => {
    window.location = "tel:(800) 815-2258"
}

export const deleteAccount = (setDeleteProfile) => {
    setDeleteProfile(true);
}

export const editProfile = (setOpenEditProfileForm) => {
    setOpenEditProfileForm(true);
}

export const styles = (theme) => {
    if (theme && theme?.background && theme?.palette) {
        return {
            pageBackground: {
                backgroundColor: theme?.palette?.background?.paper,
                minHeight: '100vh',
                position: 'relative',
            },
            body: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                [theme?.breakpoints?.down('sm')]: {
                    minHeight: `calc(100vh - ${theme?.spacing(7)})`,
                },
            },
            toolbar: {
                paddingLeft: theme?.spacing(2),
                paddingRight: theme?.spacing(2),
            },
            divider: {
                marginTop: theme?.spacing(3),
                marginBottom: theme?.spacing(3),
            },
            rotate: {
                animation: '2500ms $spin linear infinite',
            },
            '@keyframes spin': {
                '100%': {
                    transform: 'rotate(360deg)',
                },
            },
            uiText: {
                color: theme?.palette?.primary?.main,
            },
        }
    }
}

export const transform = (value) => {
    // Handle null, undefined, or empty string
    if (!value || typeof value !== 'string') {
        return 'U';
    }
    
    let text = '';
    const wordArray = value.split(' ').filter(word => word.length > 0);
    
    if (wordArray.length > 1) {
        for (let i = 0; i < wordArray.length; i++) {
            text += wordArray[i].substring(0, 1).toUpperCase();
        }
        return text;
    }
    
    if (wordArray.length === 1 && wordArray[0].length >= 2) {
        return wordArray[0].substring(0, 2).toUpperCase();
    }
    
    if (wordArray.length === 1 && wordArray[0].length === 1) {
        return wordArray[0].substring(0, 1).toUpperCase();
    }
    
    return 'U';
}

export const NavigationMenu = () => {
    const theme = useTheme();
    const classes = useClasses(styles);
    const { setDrawerOpen, setForcedClosed, setVariant } = useDrawer();
    const md = useMediaQuery(theme?.breakpoints?.up('md'));

    const navigate = useNavigate();

    const { avatarText, setAvatarText } = useContext(StateContext);
    const { name, setName } = useContext(StateContext);
    const { email, setEmail } = useContext(StateContext);

    const { t, i18n } = useTranslation();
    const location = useLocation();
    const { toogleShow } = useContext(StateContext);
    const { toggleEdit } = useContext(StateContext);
    const { pageHeader } = useContext(StateContext);
    const [deleteProfile, setDeleteProfile] = useState(false);
    const { setIsAuthenticated } = useContext(StateContext);
    const { deviceUniqueIdentificationNumber, setDeviceUniqueIdentificationNumber } = useContext(StateContext);
    const [, setUserRoles] = useState([]);

    const { logout: keycloakLogout } = useKeycloak();
    const oktaAuthState = useOktaAuth();
    const oktaAuth = oktaAuthState?.oktaAuth || null;
    const authMode = getAuthMode();

    const { setShowChangePasswordDailog } = useContext(StateContext);
    const [openEditProfileForm, setOpenEditProfileForm] = useState(false);

    const [displayBackButton, setDisplayBackButton] = useState(false);

    const currentUserData = PortalClientStorage.getData('currentUser', true);
    let hasNotificationsRole = false;
    if (currentUserData) {
        try {
            const parsedCurrentUserData =
                typeof currentUserData === 'string' ? JSON.parse(currentUserData) : currentUserData;
            if (Array.isArray(parsedCurrentUserData)) {
                hasNotificationsRole = parsedCurrentUserData.includes('YukonSupport')
                    || parsedCurrentUserData.includes('OTADLMAdmin');
            }
        } catch {
            hasNotificationsRole = false;
        }
    }
    hasNotificationsRole = hasNotificationsRole && isDevPortalEnvironment();

    const changeLanguageHandler = (e) => {
        const languageValue = e.target.value;
        i18n.changeLanguage(languageValue);
        i18n.reloadResources();
    };

    useEffect(() => {
        if (
            location &&
            (location.search.includes('add-rule') ||
                location.search.includes('edit-rule') ||
                location.search.includes('video_display') ||
                location.pathname.includes('new-job')) 
        ) {
            setDisplayBackButton(true);
        } else {
            setDisplayBackButton(false);
        }
    }, [location && location.search, location && location.pathname ]);

    useEffect(() => {
        const user = JSON.parse(window.localStorage.getItem('userDetails'));
        const roles = JSON.parse(window.localStorage.getItem('currentUser'));
        const name = user && user.name;
        const email = user && user.email;
        setName(name);
        setEmail(email);
        setUserRoles(roles || []);
        if (!!name) {
            const avatarText = transform(name);
            setAvatarText(avatarText);
        }
    }, []);



    /**
     * Handles user logout for both Okta and Keycloak authentication modes
     * 
     * OKTA MODE:
     * - Calls oktaAuth.signOut() which redirects to Okta's logout endpoint
     * - Okta clears the session cookie on their side
     * - Redirects back to /logout/callback, then to /login
     * - Critical for automated tests: ensures next login shows the form
     * 
     * KEYCLOAK MODE:
     * - Clears backend session via logoutUser()
     * - Clears local storage and authentication state
     * - Calls keycloakLogout() which handles Keycloak-specific cleanup
     */
    const logOut = async () => {
        if (authMode === AUTH_MODES.OKTA && oktaAuth) {
            try {
                // IMPORTANT: Use await to ensure signOut completes properly
                // signOut redirects to Okta logout endpoint, which clears session cookies
                // Then redirects back to postLogoutRedirectUri
                await oktaAuth.signOut({
                    postLogoutRedirectUri: `${window.location.origin}/logout/callback`,
                });
                // Note: Code after this may not execute due to browser redirect
            } catch (err) {
                console.error('❌ Error during Okta signOut:', err);
                // Fallback: manually clear state and redirect
                logoutUser();
                setIsAuthenticated(false);
                LocalStorage.clearAuthCredentials();
                window.location.href = '/login';
            }
        } else if (authMode === AUTH_MODES.KEYCLOAK && keycloakLogout) {
            // Clear backend session and local state BEFORE Keycloak logout
            logoutUser();
            setIsAuthenticated(false);
            LocalStorage.clearAuthCredentials();
            window.localStorage.removeItem('authMode');
            // Keycloak logout (will redirect to Keycloak logout page)
            keycloakLogout();
        } else {
            // Fallback for unknown auth mode
            logoutUser();
            setIsAuthenticated(false);
            LocalStorage.clearAuthCredentials();
            window.localStorage.removeItem('authMode');
            window.location.href = '/login';
        }
    };

    const handleBackClicked = () => {
        if (location.search.includes('add')) {
            toogleShow();
        }
        if (location.search.includes('edit')) {
            toggleEdit();
        }
        if (location.pathname.includes('new-job')) {
            navigate(-1);
        }
    };

    /**
     * Handle Change Password Action
     * 
     * Routes to different password change flows based on authentication mode:
     * - OKTA MODE: Navigate to /forgot-password route (Okta's password reset)
     * - KEYCLOAK MODE: Show Keycloak change password dialog
     */
    const changePassword = () => {
        if (authMode === AUTH_MODES.OKTA) {
            // Okta mode: Navigate to forgot-password page
            navigate('/forgot-password');
        } else {
            // Keycloak mode: Show change password dialog
            setShowChangePasswordDailog(true);
        }
    };

    return (
        <div data-testid="navigation-menu-wrapper">
            <div data-testid="app-bar-wrapper">
                <AppBar position={'fixed'}>
                    <Toolbar className={classes?.toolbar}>
                        {md ? (
                            <IconButton
                                color={'inherit'}
                                onClick={() => {
                                    setDrawerOpen(true);
                                    setForcedClosed(false);
                                    setVariant('persistent');
                                }}
                                edge={'start'}
                                style={{ marginRight: theme.spacing(3) }}
                                size="large"
                            >
                                <NavigateNextIcon />
                            </IconButton>
                        ) : (
                            <IconButton
                                color={'inherit'}
                                onClick={() => {
                                    setDrawerOpen(true);
                                }}
                                edge={'start'}
                                style={{ marginRight: theme.spacing(3) }}
                                size="large"
                            >
                                <Menu />
                            </IconButton>
                        )}

                        <Typography color={'inherit'} className="page-heading">
                            {displayBackButton && <KeyboardBackspace onClick={() => handleBackClicked()} />}
                            {pageHeader}
                        </Typography>
                      
                        <Spacer />
                        {false && (
                            <select className="languageSelector" onChange={changeLanguageHandler}>
                                <option value="en">
                                    {t('translation:pages.navigation_menu.languageSelectorEnglish')}
                                </option>
                                <option value="fr">
                                    {t('translation:pages.navigation_menu.languageSelectorFrench')}
                                </option>
                            </select>
                        )}
                        <Spacer />
                        {hasNotificationsRole && <NotificationBell />}
                        <UserMenu
                            avatar={<Avatar>{avatarText}</Avatar>}
                            menuTitle={name}
                            menuSubtitle={email}
                            menuGroups={[
                                {
                                    items: [
                                        {
                                            title: t('translation:pages.navigation_menu.titleChangePassword'),
                                            icon: <Lock />,
                                            onClick: changePassword,
                                        },
                                        {
                                            title: t('translation:pages.navigation_menu.titleLicenseManagement'),
                                            icon: <LicenseIcon />,
                                            onClick: () => navigate('/license-management'),
                                        },
                                        {
                                            title: t('translation:pages.navigation_menu.titleLogout'),
                                            icon: <ExitToApp />,
                                            onClick: logOut,
                                        },
                                        {
                                            title: t('translation:pages.navigation_menu.titleEditProfile'),
                                            icon: <ModeEditIcon />,
                                            onClick: () => editProfile(setOpenEditProfileForm),
                                        },
                                        {
                                            title: t('translation:pages.navigation_menu.titleDeleteAccount'),
                                            icon: <DeleteForeverTwoToneIcon />,
                                            onClick: () => deleteAccount(setDeleteProfile),
                                            divider: true,
                                        },
                                        {
                                            title: t('translation:pages.navigation_menu.titleContactUs'),
                                            divider: true,
                                        },
                                    ],
                                },
                                {
                                    items: [
                                        {
                                            title: 'eas-support@eaton.com',
                                            icon: <MailOutlineIcon />,
                                            onClick: openEmail,
                                        },
                                        {
                                            title: '(800) 815-2258',
                                            icon: <CallIcon />,
                                            onClick: callSupportContact,
                                        },
                                    ],
                                    fontColor: '#007bc1',
                                    iconColor: '#007bc1',
                                },
                            ]}
                            MenuProps={{
                                anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
                                transformOrigin: { horizontal: 'right', vertical: 'top' },
                            }}
                        />
                    </Toolbar>
                </AppBar>
            </div>

            <div className={classes?.body} data-testid="navigation-menu-body">
                {deleteProfile && <DeleteProfileComponent componentProp={setDeleteProfile} />}

                {openEditProfileForm && (
                    <EditProfile formProps={setOpenEditProfileForm} setAvatarText={setAvatarText} setName={setName} />
                )}
            </div>
        </div>
    );
};